import type { FormInstance, FormListFieldData } from "antd";
import FlexContainerLayout from "components/atoms/FlexContainerLayout";
import DomainDataPreviewList from "components/molecules/DomainDataPreviewList";
import PicklistFieldProperties from "components/molecules/PicklistFieldProperties";
import { useAppDispatch, useAppSelector } from "hooks";
import { useCallback, useEffect, useState } from "react";
import actions from "store/actions";

interface SchemaPicklistFieldPropertiesProps {
    formRef: React.RefObject<FormInstance>;
    selectedFieldRow: FormListFieldData;
    readOnly?: boolean;
    getFormErrors: (name: string) => Object;
    getBusinessAreaDataForBusinessAreaResult?: IResultState;
    businessAreaDataList?: IBusinessAreaData[];
}

export default function SchemaPicklistFieldProperties({
    formRef,
    selectedFieldRow,
    readOnly,
    getFormErrors,
    getBusinessAreaDataForBusinessAreaResult,
    businessAreaDataList,
}: SchemaPicklistFieldPropertiesProps) {
    const [domainDataItems, setDomainDataItems] = useState<any[]>([]);
    const [fetchingDomainData, setFetchingDomainData] = useState(false);
    const [dataDomainId, setDataDomainId] = useState<string | undefined>(undefined);
    const [picklistType, setPicklistType] = useState<string>("dynamic");

    const domainData = useAppSelector((state) => state.formDomainDataPicklistField.domainData);

    const dispatch = useAppDispatch();
    const getPicklistDomainData = useCallback((dataDomainId: string, pageKey: any) => dispatch(actions.getPicklistDomainDataRequest(dataDomainId, pageKey)), [dispatch]);

    const getDomainDataItems = useCallback(
        (dataDomainId: string) => {
            if (
                domainData &&
                domainData[dataDomainId] &&
                domainData[dataDomainId].getDomainDataResult &&
                domainData[dataDomainId].getDomainDataResult.inProgress === false &&
                domainData[dataDomainId].domainDataResult &&
                domainData[dataDomainId].domainDataResult.Items &&
                domainData[dataDomainId].domainDataResult.Items.length > 0
            ) {
                setDomainDataItems(domainData[dataDomainId].domainDataResult.Items.slice(0, 99));
            } else {
                getPicklistDomainData?.(dataDomainId, null);
                setFetchingDomainData(true);
                setDomainDataItems([]);
            }
        },
        [domainData, getPicklistDomainData],
    );

    const getStaticPickListItems = useCallback(() => {
        let staticPicklist: any[] = formRef.current?.getFieldValue(["fields", selectedFieldRow.name, "staticPicklist"]) || [];
        let items = staticPicklist.map((item, index) => ({ ...item, sNo: index + 1, domainDataId: index + 1 }));
        setDomainDataItems(items);
    }, [formRef, selectedFieldRow.name]);

    const onPicklistTypeChange = useCallback(
        (picklistType: string) => {
            setPicklistType(picklistType);
            if (picklistType === "static") {
                getStaticPickListItems();
            } else if (picklistType === "dynamic") {
                let dataDomainId = formRef.current?.getFieldValue(["fields", selectedFieldRow.name, "dataDomainId"]);
                setDomainDataItems([]);
                if (dataDomainId) {
                    setDataDomainId(dataDomainId);
                    getDomainDataItems(dataDomainId);
                }
            }
        },
        [formRef, selectedFieldRow.name, getStaticPickListItems, getDomainDataItems],
    );

    const onDataDomainChange = useCallback(
        (dataDomainId: string) => {
            setDataDomainId(dataDomainId);
            getDomainDataItems(dataDomainId);
        },
        [getDomainDataItems],
    );

    useEffect(() => {
        setDomainDataItems([]);
        setFetchingDomainData(false);
        setDataDomainId(undefined);
        setPicklistType("dynamic");
        let picklistType = formRef?.current?.getFieldValue(["fields", selectedFieldRow.name, "picklistType"]) ?? "dynamic";
        setPicklistType(picklistType);
        if (picklistType === "dynamic") {
            let dataDomainId = formRef?.current?.getFieldValue(["fields", selectedFieldRow.name, "dataDomainId"]);
            if (dataDomainId) {
                setDataDomainId(dataDomainId);
                getDomainDataItems(dataDomainId);
            }
        } else if (picklistType === "static") {
            getStaticPickListItems();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFieldRow.name]);

    useEffect(() => {
        if (
            domainData &&
            dataDomainId &&
            domainData[dataDomainId] &&
            domainData[dataDomainId].getDomainDataResult &&
            domainData[dataDomainId].getDomainDataResult.inProgress === false &&
            domainData[dataDomainId].domainDataResult &&
            domainData[dataDomainId].domainDataResult.Items
        ) {
            if (picklistType === "dynamic") {
                setDomainDataItems(domainData[dataDomainId].domainDataResult.Items.slice(0, 99));
                setFetchingDomainData(false);
            }
        }
    }, [domainData, dataDomainId, picklistType]);

    return (
        <FlexContainerLayout contentClassName="gap-4">
            <FlexContainerLayout>
                <PicklistFieldProperties
                    onDataDomainChange={onDataDomainChange}
                    onPicklistTypeChange={onPicklistTypeChange}
                    formRef={formRef}
                    selectedFieldRow={selectedFieldRow}
                    readOnly={readOnly}
                    getBusinessAreaDataForBusinessAreaResult={getBusinessAreaDataForBusinessAreaResult}
                    businessAreaDataList={businessAreaDataList}
                    getFormErrors={getFormErrors}
                />
            </FlexContainerLayout>
            {domainDataItems && domainDataItems.length > 0 && (
                <DomainDataPreviewList
                    header="Picklist Preview"
                    headerClassName="font-bold text-md mb-1"
                    showRecordsHeader={false}
                    domainDataList={domainDataItems}
                    loading={fetchingDomainData ? { description: "Loading data..." } : false}
                    emptyText="No picklist item"
                />
            )}
        </FlexContainerLayout>
    );
}
